import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { TiptapEditor } from '@/components/tiptap-editor/tiptap-editor';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import type { Editor, NodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import React, { useState } from 'react';

const ZyFootnoteNodeView: React.FC<NodeViewProps> = ({ node, editor, getPos, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const [isOpen, setIsOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [localEditor, setLocalEditor] = useState<Editor | null>(null);
    const [footnoteNumber, setFootnoteNumber] = useState(node.attrs.number || '');

    const handleSave = () => {
        if (localEditor) {
            const content = localEditor.getJSON().content || [];
            const pos = getPos();

            if (typeof pos !== 'number') {
                setErrorMessage('Unable to save footnote at this position.');
                throw new Error('Invalid position for footnote node.');
            }

            // Extract only inline content from paragraphs
            const inlineContent: any[] = [];
            content.forEach(node => {
                if (node.type === 'paragraph' && node.content) {
                    inlineContent.push(...node.content);
                } else if (node.type !== 'paragraph') {
                    inlineContent.push(node);
                }
            });

            // Replace the content of this node and update attributes
            editor
                .chain()
                .focus()
                .command(({ tr }) => {
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        number: footnoteNumber,
                    });
                    const nodes = inlineContent.map(item => editor.schema.nodeFromJSON(item));
                    tr.replaceWith(pos + 1, pos + node.nodeSize - 1, nodes);
                    return true;
                })
                .run();
        }
        setIsOpen(false);
    };

    return (
        <>
            <ZyNodeViewWrapper
                decorations={decorations}
                as="span"
                className="zy-footnote-wrapper"
                style={{ display: 'inline' }}
            >
                <sup
                    className="footnote-anchor rounded bg-blue-200 px-2 hover:bg-blue-300"
                    onClick={() => {
                        if (!isEditorReadonly) {
                            setIsOpen(true);
                        }
                    }}
                    contentEditable={false}
                >
                    {node.attrs.number || '?'}
                </sup>
                {/* Hidden content - not displayed in editor */}
                <span style={{ display: 'none' }}>
                    <NodeViewContent />
                </span>
            </ZyNodeViewWrapper>

            <Dialog
                modal={false}
                open={isOpen}
                onOpenChange={open => {
                    setIsOpen(open);
                    if (!open) {
                        setErrorMessage('');
                    }
                }}
            >
                <DialogContent className="flex max-h-[80vh] max-w-4xl sm:max-w-4xl flex-col overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Edit Footnote Reference</DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="footnote-number">Footnote Number</Label>
                            <Input
                                id="footnote-number"
                                type="text"
                                value={footnoteNumber}
                                onChange={e => setFootnoteNumber(e.target.value)}
                                disabled={isEditorReadonly}
                                placeholder="Enter footnote number"
                            />
                        </div>
                        <div className="h-56 rounded-md border p-4">
                            {/* TODO: Show only inline tool buttons */}
                            <TiptapEditor
                                useCollaboration={false}
                                content={{
                                    type: 'doc',
                                    content: [
                                        {
                                            type: 'paragraph',
                                            content: node.content.content.map(n => n.toJSON()),
                                        },
                                    ],
                                }}
                                onUpdate={editor => {
                                    setLocalEditor(editor);
                                }}
                            />
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                            {errorMessage}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isEditorReadonly}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ZyFootnoteNodeView;
